import { computed } from 'vue';

/**
 * Composable for accessing debug mode state throughout the application.
 *
 * Debug mode is automatically enabled during development (npm run dev) and when
 * running on localhost/private networks. It can also be manually controlled via
 * the VITE_DEBUG_MODE environment variable.
 *
 * @returns {Object} Debug mode utilities
 * @property {boolean} isDebug - Whether the application is running in debug mode
 * @property {string} debugReason - Reason why debug mode is enabled (for debugging)
 * @property {Function} logDebug - Console log function that only logs in debug mode
 * @property {Function} logDebugWarn - Console warn function that only logs in debug mode
 * @property {Function} logDebugError - Console error function that only logs in debug mode
 */
export function useDebugMode() {
  /**
   * Check if the application is running in debug mode.
   * Debug mode is automatically enabled during development (npm run dev) and can be
   * manually controlled via the VITE_DEBUG_MODE environment variable.
   *
   * Automatic detection logic:
   * - Enabled if NODE_ENV is 'development' (during npm run dev)
   * - Enabled if running on localhost (127.0.0.1 or ::1)
   * - Can be overridden by VITE_DEBUG_MODE environment variable
   *
   * @returns {boolean} True if debug mode is enabled, false otherwise
   */
  const isDebug = computed(() => {
    // First check for manual override via environment variable
    const manualDebugMode = import.meta.env.VITE_DEBUG_MODE;
    if (manualDebugMode !== undefined) {
      return manualDebugMode === 'true' || manualDebugMode === '1' || Boolean(manualDebugMode);
    }

    // Auto-detect development mode
    const isDevelopment =
      import.meta.env.MODE === 'development' || import.meta.env.NODE_ENV === 'development';

    // Check if running on localhost (common development scenario)
    const isLocalhost =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname === '::1' ||
        window.location.hostname.startsWith('192.168.') ||
        window.location.hostname.startsWith('10.'));

    // Enable debug mode if in development or on localhost
    return isDevelopment || isLocalhost;
  });

  /**
   * Log messages only when debug mode is enabled.
   * This function acts as a conditional console.log that only outputs in debug mode.
   *
   * @param args - Arguments to log
   */
  const logDebug = (...args: unknown[]) => {
    if (isDebug.value) {
      console.log('[DEBUG]', ...args);
    }
  };

  /**
   * Log warnings only when debug mode is enabled.
   *
   * @param args - Arguments to log as warnings
   */
  const logDebugWarn = (...args: unknown[]) => {
    if (isDebug.value) {
      console.warn('[DEBUG WARN]', ...args);
    }
  };

  /**
   * Log errors only when debug mode is enabled.
   *
   * @param args - Arguments to log as errors
   */
  const logDebugError = (...args: unknown[]) => {
    if (isDebug.value) {
      console.error('[DEBUG ERROR]', ...args);
    }
  };

  /**
   * Get the reason why debug mode is enabled (for debugging purposes).
   * This is useful to understand what triggered debug mode activation.
   *
   * @returns {string} Reason for debug mode being enabled
   */
  const debugReason = computed(() => {
    const manualDebugMode = import.meta.env.VITE_DEBUG_MODE;
    if (manualDebugMode !== undefined) {
      return `Manual override: VITE_DEBUG_MODE=${manualDebugMode}`;
    }

    const isDevelopment =
      import.meta.env.MODE === 'development' || import.meta.env.NODE_ENV === 'development';
    if (isDevelopment) {
      return 'Development mode (npm run dev)';
    }

    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1') {
        return `Localhost: ${hostname}`;
      }
      if (hostname.startsWith('192.168.') || hostname.startsWith('10.')) {
        return `Private network: ${hostname}`;
      }
    }

    return 'Unknown reason';
  });

  return {
    isDebug,
    debugReason,
    logDebug,
    logDebugWarn,
    logDebugError,
  };
}
